import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

interface NotFoundProps {
  title?: string;
  description?: string;
  showAnimation?: boolean;
}

const NotFound: React.FC<NotFoundProps> = ({ title, description, showAnimation = true }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [logoScale, setLogoScale] = useState(1);

  useEffect(() => {
    if (showAnimation) {
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(true);
    }
  }, [showAnimation]);

  // Logo breathing animation
  useEffect(() => {
    if (!showAnimation) return;

    const interval = setInterval(() => {
      setLogoScale((prev) => (prev === 1 ? 1.05 : 1));
    }, 2000);

    return () => clearInterval(interval);
  }, [showAnimation]);

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="relative h-dvh md:min-h-screen w-full overflow-hidden bg-black text-white flex items-center justify-center">
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-blue-600/8 to-blue-900/10" />
      {/* Background effects */}
      <div className="absolute inset-0">
        {/* Primary ambient sphere */}
        <div className="absolute top-1/4 left-1/4 w-32 h-32 sm:w-48 sm:h-48 lg:w-64 lg:h-64 bg-gradient-to-br from-cyan-400/20 via-blue-500/15 to-blue-600/10 rounded-full blur-3xl" />

        {/* Secondary ambient sphere */}
        <div className="absolute bottom-1/4 right-1/4 w-40 h-40 sm:w-56 sm:h-56 lg:w-72 lg:h-72 bg-gradient-to-tl from-blue-400/15 via-cyan-500/12 to-teal-500/8 rounded-full blur-3xl" />

        {/* Floating glass orbs */}
        <div
          className="absolute top-1/3 left-1/6 w-16 h-16 sm:w-24 sm:h-24 lg:w-32 lg:h-32 bg-gradient-to-br from-cyan-400/25 via-blue-400/15 to-teal-400/10 rounded-full blur-xl backdrop-blur-sm border border-white/5"
          style={{
            animation: 'float 8s ease-in-out infinite',
          }}
        />

        <div
          className="absolute bottom-1/3 right-1/6 w-20 h-20 sm:w-28 sm:h-28 lg:w-36 lg:h-36 bg-gradient-to-tl from-blue-400/20 via-cyan-400/12 to-blue-600/8 rounded-full blur-xl backdrop-blur-sm border border-white/5"
          style={{
            animation: 'float 10s ease-in-out infinite reverse',
          }}
        />
      </div>

      {/* Main content container */}
      <div
        className={`relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl transition-all duration-1500 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        {/* 404 Number with breathing animation */}
        <div
          className="mb-8 sm:mb-10 lg:mb-12 transition-transform duration-1000 ease-in-out"
          style={{
            transform: `scale(${logoScale})`,
            filter: 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.1))',
          }}
        >
          <h1 className="text-8xl sm:text-9xl lg:text-[12rem] font-thin text-white/90 tracking-tight">
            404
          </h1>
        </div>

        {/* Title */}
        <div
          className={`transition-all duration-1500 delay-300 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-thin text-white mb-4 sm:mb-6 tracking-tight">
            {title || t('notFound.title', 'Page Not Found')}
          </h2>
        </div>

        {/* Description */}
        <div
          className={`transition-all duration-1500 delay-500 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-400 font-light leading-relaxed max-w-2xl mx-auto px-4 mb-8 sm:mb-10 lg:mb-12">
            {description ||
              t(
                'notFound.description',
                'The page you are looking for does not exist or has been moved.',
              )}
          </p>
        </div>

        {/* Go Home Button */}
        <div
          className={`transition-all duration-1500 delay-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <button
            onClick={handleGoHome}
            className="group relative px-8 py-3 sm:px-10 sm:py-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-full text-white font-light text-base sm:text-lg transition-all duration-300 backdrop-blur-sm"
          >
            <span className="relative z-10">{t('notFound.goHome', 'Go Home')}</span>
            <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>
        </div>

        {/* Subtle progress indicator */}
        {showAnimation && (
          <div
            className={`mt-12 sm:mt-14 lg:mt-16 transition-all duration-1500 delay-900 ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="w-32 h-1 bg-gray-800 rounded-full mx-auto overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-white/20 to-white/40 rounded-full"
                style={{
                  animation: 'shimmer 3s ease-in-out infinite',
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Subtle glass reflection */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/2 via-transparent to-white/1 backdrop-blur-3xl" />

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent" />
    </div>
  );
};

export default NotFound;
